import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "../../Sidebar/Pages/AccountSetting.css";
import { fetchAccountData, fetcOrganizationData, fetchSubOrgDetails, fetchMemberDetails } from "../../actioncreators/actioncreators";

const AccountSetting = () => {
  const [data, setData] = useState(null);
  const [subOrgData, setSubOrgData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // The org a Super Admin has selected in the top-bar filter. For a normal user
  // this is empty and the backend falls back to their own JWT org.
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);
  // Full selected node (org or sub_org). When the Super Admin drilled into a
  // sub-org, this carries type:'sub_org' + sub_org_id so the profile page can
  // show that sub-org's own details instead of the parent org's.
  const superAdminSelectedOrgData = useSelector((state) => state.superAdminSelectedOrgData);
  const drilldownSubOrgId = superAdminSelectedOrgData?.type === 'sub_org' ? superAdminSelectedOrgData?.sub_org_id : null;

  const fetchDashboardData1 = async () => {
    setIsLoading(true);
    try {
      let targetSubOrgId = drilldownSubOrgId;

      // No header drilldown - this may still be a user who is DIRECTLY
      // logged in as a sub-org-scoped user (e.g. a Sub-Org Admin/Manager),
      // not viewing via the Global Filter at all. loginResponse doesn't
      // carry sub_org_id, so resolve it from the caller's own member
      // details (an empty/omitted userId there defaults to "myself" and
      // already returns sub_org_id) - without this, a directly-logged-in
      // sub-org user always fell through to the parent org's profile.
      if (!targetSubOrgId) {
        const selfResponse = await fetchMemberDetails({});
        if (selfResponse?.businessStatusCode === 2 && selfResponse?.userData?.sub_org_id) {
          targetSubOrgId = selfResponse.userData.sub_org_id;
        }
      }

      if (targetSubOrgId) {
        const response = await fetchSubOrgDetails(targetSubOrgId, superAdminSelectedOrgId || null);
        if (response?.data?.businessStatusCode === 2) {
          setSubOrgData(response.data.data);
        } else {
          setSubOrgData(null);
        }
        setData(null);
      } else {
        const response = await fetcOrganizationData(superAdminSelectedOrgId || null);
        setData(response);
        setSubOrgData(null);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Re-fetch whenever the selected org/sub-org changes, otherwise the screen
  // keeps showing the previously loaded org's details.
  useEffect(() => {
    fetchDashboardData1();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [superAdminSelectedOrgId, drilldownSubOrgId]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="account-settings-wrapper">
        <div className="theme-loading">
          <div className="theme-loader"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Sub-org profile view: a sub-organization has its own (much smaller) set of
  // fields - name, code, description, manager, status - plus the parent
  // organization it belongs to. It has no email/phone/address/org-size, so
  // those sections are not shown here. Gated on subOrgData (not just the
  // header drilldown) so it also covers a user directly logged in as a
  // sub-org-scoped user - see fetchDashboardData1's self-lookup fallback.
  if (subOrgData) {
    return (
      <div className="account-settings-wrapper">
        <div className="theme-container">
          <div className="theme-header">
            <div className="d-flex align-items-center">
              <i className="bi bi-diagram-3 me-2 orange-icon"></i>
              <h3>Sub-Organization Details</h3>
            </div>
          </div>

          <div className="theme-content">
            <div className="theme-card">
              <div className="org-identity">
                <div className="org-logo">
                  {subOrgData?.sub_org_name?.charAt(0).toUpperCase() || "S"}
                </div>
                <div>
                  <div className="d-flex align-items-center">
                    <h4>{subOrgData?.sub_org_name || "-"}</h4>
                    {subOrgData?.status && (
                      <div
                        className={`status-badge org-status-badge ${
                          String(subOrgData.status).toUpperCase() === "ACTIVE"
                            ? "status-active"
                            : "status-inactive"
                        }`}
                      >
                        <span>
                          <i
                            className={`me-1 ${
                              String(subOrgData.status).toUpperCase() === "ACTIVE"
                                ? "bi bi-check-circle-fill"
                                : "bi bi-x-circle-fill"
                            }`}
                          ></i>
                          {subOrgData.status}
                        </span>
                      </div>
                    )}
                  </div>
                  <p>{subOrgData?.sub_org_code || "-"}</p>
                </div>
              </div>

              <div className="theme-grid">
                <div className="theme-item">
                  <i className="bi bi-building"></i>
                  <span className="theme-item-content">
                    Part of: {subOrgData?.org_name || "-"}
                  </span>
                </div>

                {subOrgData?.parent_sub_org_name && (
                  <div className="theme-item">
                    <i className="bi bi-diagram-2"></i>
                    <span className="theme-item-content">
                      Parent Sub-Org: {subOrgData.parent_sub_org_name}
                    </span>
                  </div>
                )}

                <div className="theme-item">
                  <i className="bi bi-person-badge"></i>
                  <span className="theme-item-content">
                    Manager: {subOrgData?.manager_name || "Not assigned"}
                  </span>
                </div>

                {subOrgData?.manager_email && (
                  <div className="theme-item">
                    <i className="bi bi-envelope"></i>
                    <span className="theme-item-content">{subOrgData.manager_email}</span>
                  </div>
                )}

                <div className="theme-item">
                  <i className="bi bi-people"></i>
                  <span className="theme-item-content">
                    {subOrgData?.user_count != null ? `${subOrgData.user_count} members` : "-"}
                  </span>
                </div>

                <div className="theme-item">
                  <i className="bi bi-calendar"></i>
                  <span className="theme-item-content">
                    {formatDate(subOrgData?.creation_date_and_time)}
                  </span>
                </div>
              </div>

              {subOrgData?.description && (
                <div className="theme-grid" style={{ marginTop: "1rem" }}>
                  <div className="theme-item">
                    <i className="bi bi-card-text"></i>
                    <span
                      className="theme-item-content"
                      style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {subOrgData.description}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-settings-wrapper">
      <div className="theme-container">
        <div className="theme-header">
          <div className="d-flex align-items-center">
          <i className="bi bi-buildings me-2 orange-icon"></i>
          <h3>Organization Details</h3>
          </div>
        </div>

        <div className="theme-content">
          {/* Organization Information Section */}
          <div className="theme-card">

            <div className="org-identity">
              <div className="org-logo">
                {data?.organizationDetails?.org_name?.charAt(0).toUpperCase() || "O"}
              </div>
              <div>
                <div className="d-flex align-items-center">
                  <h4>{data?.organizationDetails?.org_name}</h4>
                  {/* Render the org's ACTUAL status, not a hardcoded "Active".
                      Colour + icon follow the real value so an inactive org is
                      not shown as active. Badge is hidden when status is unknown. */}
                  {data?.organizationDetails?.status && (
                    <div
                      className={`status-badge org-status-badge ${
                        String(data.organizationDetails.status).toUpperCase() === "ACTIVE"
                          ? "status-active"
                          : "status-inactive"
                      }`}
                    >
                      <span>
                        <i
                          className={`me-1 ${
                            String(data.organizationDetails.status).toUpperCase() === "ACTIVE"
                              ? "bi bi-check-circle-fill"
                              : "bi bi-x-circle-fill"
                          }`}
                        ></i>
                        {data.organizationDetails.status}
                      </span>
                    </div>
                  )}
                </div>
                <p>{data?.organizationDetails?.org_type}</p>
              </div>
            </div>

            <div className="theme-grid">
              <div className="theme-item">
                <i className="bi bi-envelope"></i>
                <span className="theme-item-content">{data?.organizationDetails?.email || "-"}</span>
              </div>

              <div className="theme-item">
                <i className="bi bi-telephone"></i>
                <span className="theme-item-content">{data?.organizationDetails?.mobile || "-"}</span>
              </div>

              <div className="theme-item">
                <i className="bi bi-people"></i>
                <span className="theme-item-content">
                  {data?.organizationDetails?.org_size
                    ? `${data?.organizationDetails?.org_size} employees`
                    : "-"}
                </span>
              </div>

              <div className="theme-item">
                <i className="bi bi-calendar"></i>
                <span className="theme-item-content">
                  {formatDate(data?.organizationDetails?.creation_date_and_time)}
                </span>
              </div>
            </div>
          </div>          {/* Section Divider */}
          <div className="section-divider"></div>

          {/* Address Information Section */}
<div className="theme-card address-section">
  <div className="section-title">
    <i className="bi bi-geo-alt me-2"></i>
    Address Information
  </div>

  {data?.organizationDetails &&
    (data.organizationDetails.address_line_1 ||
      data.organizationDetails.address_line_2 ||
      data.organizationDetails.city ||
      data.organizationDetails.pincode ||
      data.organizationDetails.state ||
      data.organizationDetails.country) ? (
    <div className="theme-grid">
    {(data.organizationDetails.address_line_1 ||
  data.organizationDetails.city ||
  data.organizationDetails.pincode) && (
  <div className="theme-item">
    <i className="bi bi-house"></i>

    <span
      className="theme-item-content"
      style={{ whiteSpace: "normal", wordBreak: "break-word" }}
    >
      {[
        data.organizationDetails.address_line_1,
        data.organizationDetails.city,
        data.organizationDetails.pincode,
      ]
        .filter(Boolean)
        .join(", ")}
    </span>
  </div>
)}



      {data.organizationDetails.state && (
        <div className="theme-item">
          <i className="bi bi-map"></i>
          <span className="theme-item-content">{data.organizationDetails.state}</span>
        </div>
      )}

      {data.organizationDetails.country && (
        <div className="theme-item">
          <i className="bi bi-globe"></i>
          <span className="theme-item-content">{data.organizationDetails.country}</span>
        </div>
      )}
    </div>
  ) : (
    <div className="empty-address">
      <i className="bi bi-geo-alt"></i>
      <p>No address information available</p>
    </div>
  )}
</div>

        </div>
      </div>
    </div>
  );
};

export default AccountSetting;
